import React from "react";
import classNames from "classnames";
import { HiOutlineMap, HiOutlineLocationMarker } from "react-icons/hi";
import { PiCubeTransparent } from "react-icons/pi";
import ShowMoreText from "react-show-more-text";
import { GoInfo } from "react-icons/go";

// components
import Button from "@/shared/Button";
import Tag from "@/shared/Tag";

// helpers
import { handleVirtualTourClick } from "@/helpers/handleVirtualTourClick";

// interfaces
import { ILocationSpaceInfo } from "./ILocationSpaceInfo";

// assets
import styles from "@/assets/styles/containers/location-space-info.module.scss";


function LocationSpaceInfo(props: ILocationSpaceInfo) {
  const {
    description,
    virtualTourUrl,
    region,
    country,
    address,
    className,
    onFloorPlanClick,
    note,
    isMobile,
    withFloorPlan,
    title,
    descriptionLines,
    onAddressClick,
    isRestricted,
    type,
    onRestrictedButtonClick,
    onVirtualTourClick,
    ...rest
  } = props;

  const locationSpaceInfoClassName = classNames(
    styles["locl-location-space-info"],
    className,
    { [styles["locl-location-space-info_space-page"]]: type === "space" },
    { [styles["locl-location-space-info_location__restricted-view"]]: isRestricted && type === "location" },
  );

  const showMoreLines = isMobile ? 4 : 6;

  // methods
  const handleVirtualTourButton = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestricted) {
      onRestrictedButtonClick && onRestrictedButtonClick(e);

      return;
    }

    handleVirtualTourClick(e, virtualTourUrl || "")
    onVirtualTourClick && onVirtualTourClick();
  }

  const handleDescriptionBlockClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestricted && type === "location") {
      onRestrictedButtonClick && onRestrictedButtonClick(e);
    }
  }

  const handleFloorPlanClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isRestricted && type === "location") {
      onRestrictedButtonClick && onRestrictedButtonClick(e);
      
      return;
    }

    onFloorPlanClick && onFloorPlanClick();
  }

  // render
  return (
    <div className={locationSpaceInfoClassName} {...rest}>
      <div className={styles["locl-location-space-info__header"]}>
        {
          title ? (
            <div className={styles["locl-location-space-info__title"]}>
              { title }
            </div>
          ) : null
        }
        <div className={styles["locl-location-space-info__header-actions"]}>
          { virtualTourUrl && (
            <Button
              type="secondary"
              success
              iconRight={<PiCubeTransparent className={styles["locl-location-space-info__header-actions__btn-icon"]} />}
              linkClassName={styles["locl-location-space-info__header-actions__btn-link"]}
              className={styles["locl-location-space-info__header-actions__btn"]}
              href={virtualTourUrl}
              onClick={handleVirtualTourButton}
              data-testid="locl-location-space-info-floor-virtual-tour-btn"
            >
              Virtual Tour
            </Button>
          )}
          {
            withFloorPlan && (
              <Button
                type="secondary"
                success
                onClick={handleFloorPlanClick}
                iconRight={<HiOutlineMap className={styles["locl-location-space-info__header-actions__btn-icon"]} />}
                className={styles["locl-location-space-info__header-actions__btn"]}
                data-testid="locl-location-space-info-floor-plan-btn"
              >
                Floor Plan
              </Button>
            )
          }
        </div>
      </div>
      {note && (
        <Tag
          color="green"
          icon={<GoInfo className="locl-location-space-info__address-note__icon" />}
          className={styles["locl-location-space-info__note"]}
          data-testid="locl-location-space-note"
        >
          {note}
        </Tag>
      )}
      <div className={styles["locl-location-space-info__description"]}>
        <h3 className={styles["locl-location-space-info__description-title"]}>Description</h3>
        <div
          className={styles["locl-location-space-info__description-label"]}
          onClick={handleDescriptionBlockClick}
          data-testid="locl-location-space-description"
        >
          <ShowMoreText
            lines={descriptionLines || showMoreLines}
            more="Read more"
            less="Read less"
            className="locl-show-more"
            anchorClass={styles["locl-location-space-info__description-controls"]}
            keepNewLines={true}
          >
            {description}
          </ShowMoreText>
        </div>
      </div>
      <div className={styles["locl-location-space-info__address"]}>
        {(region || address) ? (
          <div
            className={classNames(
              styles["locl-location-space-info__address-item"],
              { [styles["locl-location-space-info__address-item_clickable"]]: onAddressClick }
            )}
            onClick={() => onAddressClick ? onAddressClick() : null}
          >
            <HiOutlineLocationMarker className={styles["locl-location-space-info__address-item-icon"]} />
            <p
              className={styles["locl-location-space-info__address-item-label"]}
              data-testid="locl-location-space-region"
            >
              { address ? address : `${region}, ${country}`}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default LocationSpaceInfo;
